import React from 'react';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white py-12 px-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate('/')}
            className="mb-4 text-[#54edb6] hover:text-white transition-colors"
          >
            ← Back to Home
          </button>
          <h1 className="text-4xl font-bold mb-4">Privacy Policy</h1>
          <p className="text-gray-400">Last updated: December 2024</p>
        </div>

        {/* Content */}
        <div className="prose prose-invert max-w-none space-y-6">
          
          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">1. Introduction</h2>
            <p className="text-gray-300 leading-relaxed">
              At Immigrantvli.com ("we," "our," or "us"), we are committed to protecting your privacy and personal information. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">2. Information We Collect</h2>
            
            <h3 className="text-xl font-semibold text-white mb-3">Personal Information</h3>
            <p className="text-gray-300 leading-relaxed mb-3">We collect information you provide directly to us, including:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Name and email address (for account registration)</li>
              <li>Payment information (processed securely through Stripe)</li>
              <li>Immigration case details and documents you upload</li>
              <li>Communication preferences and support inquiries</li>
            </ul>

            <h3 className="text-xl font-semibold text-white mb-3 mt-6">Automatically Collected Information</h3>
            <p className="text-gray-300 leading-relaxed mb-3">When you use our service, we automatically collect:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Usage data and analytics</li>
              <li>Device information and IP address</li>
              <li>Browser type and operating system</li>
              <li>Session data and user interactions</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-300 leading-relaxed mb-3">We use the information we collect to:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li>Provide and improve our AI-powered immigration services</li>
              <li>Process your documents and generate case analysis</li>
              <li>Manage your account and subscription</li>
              <li>Send you important service updates and communications</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Ensure platform security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-gray-300 leading-relaxed mb-3">
              We do not sell, rent, or share your personal information with third parties, except in the following circumstances:
            </p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>With your consent:</strong> When you explicitly authorize us to share information</li>
              <li><strong>Service providers:</strong> With trusted third-party service providers who assist us in operating our platform (e.g., payment processing, cloud hosting)</li>
              <li><strong>Legal requirements:</strong> When required by law, court order, or to protect our rights and users' safety</li>
              <li><strong>Business transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">5. Data Security</h2>
            <p className="text-gray-300 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect your personal information against 
              unauthorized access, alteration, disclosure, or destruction. This includes encryption, secure data transmission, 
              access controls, and regular security assessments. However, no internet transmission is completely secure, 
              and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">6. Data Retention</h2>
            <p className="text-gray-300 leading-relaxed">
              We retain your personal information for as long as necessary to provide our services, comply with legal obligations, 
              resolve disputes, and enforce our agreements. You may request deletion of your account and personal information 
              at any time, subject to certain legal and operational requirements.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">7. Your Rights and Choices</h2>
            <p className="text-gray-300 leading-relaxed mb-3">You have the following rights regarding your personal information:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request transfer of your information to another service</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Restriction:</strong> Request limitation on how we process your information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">8. Third-Party Services</h2>
            <p className="text-gray-300 leading-relaxed mb-3">Our platform integrates with third-party services:</p>
            <ul className="list-disc list-inside text-gray-300 space-y-2 ml-4">
              <li><strong>Stripe:</strong> For secure payment processing</li>
              <li><strong>Google OAuth:</strong> For authentication (optional)</li>
              <li><strong>OpenAI:</strong> For AI-powered document analysis and content generation</li>
              <li><strong>Google Cloud Vision:</strong> For OCR and document scanning</li>
            </ul>
            <p className="text-gray-300 leading-relaxed mt-3">
              These services have their own privacy policies, and we encourage you to review them.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">9. Children's Privacy</h2>
            <p className="text-gray-300 leading-relaxed">
              Our service is not intended for children under the age of 13. We do not knowingly collect personal information 
              from children under 13. If we become aware that we have collected personal information from a child under 13, 
              we will take steps to delete such information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">10. International Data Transfers</h2>
            <p className="text-gray-300 leading-relaxed">
              Your information may be transferred to and processed in countries other than your own. We ensure that such 
              transfers are conducted in accordance with applicable data protection laws and with appropriate safeguards in place.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">11. Changes to This Privacy Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new 
              Privacy Policy on this page and updating the "Last updated" date. Significant changes will be communicated 
              via email or prominent notice on our platform.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-[#54edb6] mb-4">12. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed">
              If you have any questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:
            </p>
            <div className="bg-gray-900 p-4 rounded-lg mt-4">
              <p className="text-[#54edb6] font-semibold">Email: info@immigrantvli.com</p>
              <p className="text-gray-300">Address: 10 Glenlake Pkwy NE, Atlanta, GA 30328</p>
              <p className="text-gray-300 mt-2">
                For privacy-related inquiries, please include "Privacy Request" in the subject line.
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;